<template>
<div class="page-container" :style="backgroundStyle">
    <!-- 动态粒子背景 -->
    <div class="particles">
        <div v-for="n in 30" :key="n" class="particle" :style="getParticleStyle()"></div>
    </div>
    
    <!-- 渐变遮罩层 -->
    <div class="gradient-overlay"></div>
    
    <!-- 主内容区域 -->
    <div class="content-wrapper">
        <!-- 返回按钮 -->
        <router-link to="/" class="back-button">
            <LucideIcon name="arrow-left" :size="18" />
            <span>返回首页</span>
        </router-link>
        <button v-if="authenticated" class="logout-button" @click="logout">
            <LucideIcon name="log-out" :size="18" />
            <span>退出登录</span>
        </button>

        <div class="glass-card" :class="{ 'show': isLoaded }">

            <!-- 未登录：显示登录表单 -->
            <div v-if="!authenticated && !loading" class="login-section">
                <div class="page-header">
                    <div class="icon-wrapper icon-lock">
                        <LucideIcon name="lock" :size="28" color="#fff" />
                    </div>
                    <h1 class="title">管理员登录</h1>
                    <p class="subtitle">请输入账号密码以访问管理面板</p>
                </div>
                <div class="divider">
                    <div class="divider-line"></div>
                    <div class="divider-dot"></div>
                    <div class="divider-line"></div>
                </div>
                <div class="login-form">
                    <input
                        v-model="loginForm.username"
                        type="text"
                        placeholder="账号"
                        class="input-field"
                        @keyup.enter="login"
                    />
                    <input
                        v-model="loginForm.password"
                        type="password"
                        placeholder="密码"
                        class="input-field"
                        @keyup.enter="login"
                    />
                    <button class="add-btn login-btn" @click="login" :disabled="loginLoading">
                        <span>{{ loginLoading ? '登录中...' : '登录' }}</span>
                    </button>
                    <div v-if="loginError" class="login-error">
                        <LucideIcon name="x-circle" :size="16" />
                        <span>{{ loginError }}</span>
                    </div>
                </div>
            </div>

            <!-- 已登录：管理界面 -->
            <div v-if="authenticated">
            <div class="page-header">
                <div class="icon-wrapper">
                    <LucideIcon name="users" :size="28" color="#fff" />
                </div>
                <h1 class="title">用户管理</h1>
                <p class="subtitle">管理订阅用户与专属链接</p>
            </div>
            
            <!-- 分隔线 -->
            <div class="divider">
                <div class="divider-line"></div>
                <div class="divider-dot"></div>
                <div class="divider-line"></div>
            </div>

            <!-- 添加用户 -->
            <div class="add-user-section">
                <div class="add-user-form">
                    <input 
                        v-model="newUserName" 
                        type="text" 
                        placeholder="输入新用户名称"
                        class="input-field"
                        @keyup.enter="addUser"
                    />
                    <button class="add-btn" @click="addUser" :disabled="!newUserName.trim()">
                        <span>+ 添加</span>
                    </button>
                </div>
            </div>

            <!-- 节点池替换 -->
            <div class="update-sub-section">
                <div class="section-head">
                    <div class="section-label"><LucideIcon name="refresh-cw" :size="14" /> 订阅配置替换</div>
                    <router-link to="/admin/rules" class="rules-entry-link">
                        <LucideIcon name="filter" :size="14" />
                        <span>分流规则配置</span>
                    </router-link>
                </div>
                <div class="subscription-source-grid">
                    <div class="subscription-source-panel">
                        <div class="source-title"><LucideIcon name="zap" :size="14" /> Clash YAML</div>
                        <label class="yaml-file-button" :class="{ disabled: updatingSubscription }">
                            <input
                                ref="yamlFileInput"
                                type="file"
                                accept=".yaml,.yml,text/yaml,application/yaml,text/plain"
                                :disabled="updatingSubscription"
                                @change="onYamlFileSelected"
                            />
                            <LucideIcon name="file-up" :size="14" />
                            <span>{{ pendingYamlContent ? '重新选择 YAML 文件' : '选择 YAML 文件' }}</span>
                        </label>
                        <div v-if="selectedYamlFileName" class="selected-file-name">
                            {{ selectedYamlFileName }}
                        </div>

                        <fieldset v-if="pendingYamlContent" :disabled="updatingSubscription" class="target-users-block">
                            <legend class="target-title">选择配置使用方式</legend>
                            <label class="target-user-chip" :class="{ selected: subscriptionMode === 'server' }">
                                <input v-model="subscriptionMode" type="radio" value="server" name="subscription-mode" />
                                <span>使用服务端配置（默认）</span>
                            </label>
                            <label class="target-user-chip" :class="{ selected: subscriptionMode === 'raw' }">
                                <input v-model="subscriptionMode" type="radio" value="raw" name="subscription-mode" />
                                <span>完全使用上传文件</span>
                            </label>
                            <div class="target-hint">{{ subscriptionMode === 'raw' ? '整个文件原样保存并下发，不提取节点、不改写分流或其他配置。' : '提取节点和 hosts，使用服务端统一管理的分流规则。' }}</div>
                        </fieldset>

                        <!-- 用户多选 -->
                        <div class="target-users-block">
                            <div class="target-users-header">
                                <span class="target-title">选择需要替换的用户</span>
                                <button
                                    type="button"
                                    class="select-all-btn"
                                    :disabled="updatingSubscription || enabledUsers.length === 0"
                                    @click="toggleAllTargetUsers"
                                >
                                    {{ allTargetSelected ? '取消全选' : '全选' }}
                                </button>
                            </div>
                            <div class="target-users-grid">
                                <label
                                    v-for="user in enabledUsers"
                                    :key="user.id"
                                    class="target-user-chip"
                                    :class="{ 'selected': isTargetUserSelected(user.id) }"
                                >
                                    <input
                                        type="checkbox"
                                        :value="user.id"
                                        :checked="isTargetUserSelected(user.id)"
                                        :disabled="updatingSubscription"
                                        @change="toggleTargetUser(user.id)"
                                    />
                                    <span class="chip-name">{{ user.name }}</span>
                                    <span class="chip-no">{{ String(getUserNumber(user)).padStart(3, '0') }}</span>
                                </label>
                            </div>
                            <div class="target-hint">仅应用到选中的用户；未选中的用户保持原有配置。</div>
                        </div>

                        <button
                            class="update-btn replace-submit-btn"
                            :disabled="!pendingYamlContent || selectedTargetUserIds.length === 0 || updatingSubscription"
                            @click="replaceSubscriptionContent(pendingYamlContent, selectedTargetUserIds)"
                        >
                            <span>{{ updatingSubscription ? '替换中...' : `替换到选中用户（${selectedTargetUserIds.length}）` }}</span>
                        </button>
                    </div>
                </div>

                <!-- 操作结果提示 -->
                <div v-if="updateResult" class="update-result" :class="updateResult.success ? 'result-success' : 'result-error'">
                    <LucideIcon :name="updateResult.success ? 'check-circle' : 'x-circle'" :size="16" />
                    <span>{{ updateResult.message }}</span>
                </div>
            </div>

            <!-- 加载状态 -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <span>加载中...</span>
            </div>

            <!-- 用户列表 -->
            <div v-else class="users-container">
                <div 
                    v-for="(user, index) in users" 
                    :key="user.id" 
                    class="user-card"
                    :class="{ 'show': showCards, 'disabled-card': !user.enabled }"
                    :style="{ '--delay': index * 0.05 + 's' }"
                >
                    <!-- 用户信息头部 -->
                    <div class="user-header">
                        <div class="user-avatar" :class="{ 'avatar-disabled': !user.enabled }">
                            {{ user.name.charAt(user.name.length - 1) }}
                        </div>
                        <div class="user-info">
                            <span class="user-name">{{ user.name }}</span>
                            <div class="user-meta-row">
                                <span class="user-number-badge">编号 {{ String(getUserNumber(user)).padStart(3, '0') }}</span>
                                <button type="button" class="remark-btn" @click.stop="openRemarkDialog(user)"><LucideIcon name="edit-3" :size="11" /> 备注</button>
                            </div>
                            <span class="user-token">Token: {{ user.token }}</span>
                            <div v-if="user.remark" class="user-remark">备注：{{ user.remark }}</div>
                        </div>
                        <div class="user-status" :class="user.enabled ? 'status-active' : 'status-inactive'">
                            {{ user.enabled ? '启用' : '禁用' }}
                        </div>
                    </div>

                    <!-- 订阅链接区域 -->
                    <div class="subscribe-links" v-if="user.enabled">
                        <div class="link-row">
                            <span class="link-label"><LucideIcon name="zap" :size="14" /> Clash</span>
                            <button class="copy-btn" @click="copyLink(user)"><LucideIcon name="copy" :size="12" /> 复制</button>
                        </div>
                        <div class="link-url" @click="copyLink(user)">{{ getSubscribeUrl(user) }}</div>
                    </div>
                    <div class="subscribe-links disabled-notice" v-else>
                        <LucideIcon name="shield-off" :size="14" /> <span>用户已禁用，订阅链接不可用</span>
                    </div>

                    <!-- 操作按钮 -->
                    <div class="user-actions">
                        <button 
                            class="action-btn toggle-btn"
                            :class="user.enabled ? 'btn-warn' : 'btn-success'"
                            @click="toggleUser(user)"
                        >
                            {{ user.enabled ? '禁用' : '启用' }}
                        </button>
                        <button class="action-btn btn-reset" @click="resetToken(user)">
                            <LucideIcon name="key" :size="13" /> 重置Token
                        </button>
                        <button class="action-btn btn-danger" @click="confirmDeleteUser(user)">
                            <LucideIcon name="trash-2" :size="13" /> 删除
                        </button>
                    </div>

                    <!-- 访问记录 -->
                    <div class="access-logs-section">
                        <button class="access-logs-toggle" @click="toggleAccessLogs(user)">
                            <span class="logs-toggle-label"><LucideIcon name="signal" :size="14" /> 访问记录</span>
                            <LucideIcon name="chevron-down" :size="14" :class="{ 'arrow-open': user._showLogs }" class="toggle-arrow" />
                        </button>
                        <transition name="logs-slide">
                            <div v-if="user._showLogs" class="access-logs-content">
                                <div v-if="user._loadingLogs" class="logs-loading">
                                    <div class="spinner-sm"></div>
                                    <span>加载中...</span>
                                </div>
                                <div v-else-if="!user._accessLogs || user._accessLogs.length === 0" class="logs-empty">
                                    <LucideIcon name="mail-x" :size="16" /> 暂无访问记录
                                </div>
                                <div v-else class="logs-table-wrapper">
                                    <table class="logs-table">
                                        <thead>
                                            <tr>
                                                <th>IP</th>
                                                <th>归属地</th>
                                                <th>客户端</th>
                                                <th>时间</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(log, i) in user._accessLogs" :key="i">
                                                <td class="log-ip">{{ log.ip }}</td>
                                                <td class="log-location">{{ log.location }}</td>
                                                <td class="log-target">
                                                    <span class="target-tag">{{ log.target }}</span>
                                                </td>
                                                <td class="log-time">{{ formatTime(log.time) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>

                <!-- 空状态 -->
                <div v-if="users.length === 0" class="empty-state">
                    <LucideIcon name="mail-x" :size="48" color="var(--color-muted)" />
                    <p>暂无用户，请添加</p>
                </div>
            </div>

            <!-- 底部提示 -->
            <div class="footer-tip">
                <LucideIcon name="lightbulb" :size="16" color="rgba(255, 193, 7, 0.9)" />
                <span>每个用户拥有独立的订阅地址，复制后粘贴到客户端即可使用</span>
            </div>
            </div><!-- end authenticated -->
        </div>
    </div>
    
    <!-- Toast 提示 -->
    <transition name="dialog-fade">
        <div v-if="remarkDialogVisible" class="remark-dialog-overlay" @click="closeRemarkDialog">
            <div class="remark-dialog" @click.stop>
                <div class="remark-dialog-header">
                    <div>
                        <div class="remark-dialog-title">编辑备注</div>
                        <div class="remark-dialog-subtitle">{{ remarkTargetUser ? remarkTargetUser.name : '' }}</div>
                    </div>
                    <button type="button" class="remark-dialog-close" @click="closeRemarkDialog">×</button>
                </div>
                <textarea
                    v-model="remarkDraft"
                    class="remark-textarea"
                    maxlength="200"
                    placeholder="输入管理员备注，留空表示清除"
                ></textarea>
                <div class="remark-dialog-footer">
                    <span class="remark-counter">{{ remarkDraft.length }}/200</span>
                    <div class="remark-dialog-actions">
                        <button type="button" class="remark-secondary-btn" @click="closeRemarkDialog" :disabled="savingRemark">取消</button>
                        <button type="button" class="remark-primary-btn" @click="saveRemark" :disabled="savingRemark">
                            {{ savingRemark ? '保存中...' : '保存备注' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>

    <!-- 确认弹窗（替代原生 confirm） -->
    <transition name="modal">
        <div v-if="confirmDialog.visible" class="modal-overlay" @click.self="confirmDialog.visible = false">
            <div class="confirm-modal">
                <div class="confirm-header">
                    <LucideIcon :name="confirmDialog.icon" :size="32" :color="confirmDialog.iconColor" />
                    <h3>{{ confirmDialog.title }}</h3>
                    <p>{{ confirmDialog.message }}</p>
                </div>
                <div class="confirm-footer">
                    <button class="modal-cancel" @click="confirmDialog.visible = false">取消</button>
                    <button class="modal-confirm confirm-danger" @click="confirmDialog.onConfirm">{{ confirmDialog.confirmText }}</button>
                </div>
            </div>
        </div>
    </transition>

    <transition name="toast">
        <div v-if="showToast" class="toast" :class="toastType">
            <LucideIcon :name="toastType === 'success' ? 'check-circle' : 'x-circle'" :size="18" />
            <span>{{ toastMessage }}</span>
        </div>
    </transition>
</div>
</template>

<script>
import backgroundMixin from '@/mixins/background'

export default {
    name: 'UserManager',
    mixins: [backgroundMixin],
    data() {
        return {
            showCards: false,
            loading: true,
            authenticated: false,
            loginForm: { username: '', password: '' },
            loginLoading: false,
            loginError: '',
            users: [],
            newUserName: '',
            showToast: false,
            toastMessage: '',
            toastType: 'success',
            remarkDialogVisible: false,
            remarkTargetUser: null,
            remarkDraft: '',
            savingRemark: false,
            // 订阅更新
            selectedYamlFileName: '',
            pendingYamlContent: '',
            subscriptionMode: 'server',
            selectedTargetUserIds: [],
            updatingSubscription: false,
            updateResult: null,
            // 确认弹窗（替代原生 confirm）
            confirmDialog: {
                visible: false,
                title: '',
                message: '',
                icon: 'trash-2',
                iconColor: 'var(--color-danger-light)',
                confirmText: '确认',
                onConfirm: () => {}
            }
        }
    },
    watch: {
        // 用户列表加载后，为每个用户补充响应式属性
        users: {
            handler(newUsers) {
                newUsers.forEach(u => {
                    if (u._showLogs === undefined) this.$set(u, '_showLogs', false);
                    if (u._accessLogs === undefined) this.$set(u, '_accessLogs', []);
                    if (u._loadingLogs === undefined) this.$set(u, '_loadingLogs', false);
                });
            },
            immediate: true
        }
    },
    computed: {
        // 仅可被替换的启用用户
        enabledUsers() {
            return this.users.filter(u => u.enabled);
        },
        // 是否所有启用用户都已勾选
        allTargetSelected() {
            const enabled = this.enabledUsers;
            return enabled.length > 0 && enabled.every(u => this.selectedTargetUserIds.includes(u.id));
        }
    },
    methods: {
        // 鉴权检查
        async checkAuth() {
            this.loading = true;
            try {
                const res = await this.$axios.get('/api/auth/check');
                this.authenticated = !!res.data.authenticated;
                if (this.authenticated) {
                    await this.fetchUsers();
                    return;
                }
            } catch (err) {
                this.authenticated = false;
            }
            this.loading = false;
        },
        // 登录
        async login() {
            if (!this.loginForm.username || !this.loginForm.password) return;
            this.loginLoading = true;
            this.loginError = '';
            try {
                const res = await this.$axios.post('/api/auth/login', this.loginForm);
                if (res.data.success) {
                    this.authenticated = true;
                    this.loginForm = { username: '', password: '' };
                    await this.fetchUsers();
                } else {
                    this.loginError = res.data.message;
                }
            } catch (err) {
                this.loginError = err.response?.data?.message || '登录失败';
            } finally {
                this.loginLoading = false;
            }
        },
        // 退出登录
        async logout() {
            try {
                await this.$axios.post('/api/auth/logout');
            } catch (_err) { /* 退出登录即使请求失败也无影响 */ }
            this.authenticated = false;
            this.users = [];
            this.showCards = false;
            this.loading = false;
        },
        // 获取用户列表
        async fetchUsers() {
            this.loading = true;
            try {
                const res = await this.$axios.get('/api/users');
                this.users = res.data.map(user => ({
                    ...user,
                    remark: user.remark || ''
                }));
            } catch (err) {
                console.error('获取用户失败:', err);
                this.showToastMessage('获取用户列表失败', 'error');
            } finally {
                this.loading = false;
                setTimeout(() => { this.showCards = true; }, 200);
            }
        },
        // 添加用户
        async addUser() {
            if (!this.newUserName.trim()) return;
            try {
                const res = await this.$axios.post('/api/users', { name: this.newUserName.trim() });
                this.users.push({
                    ...res.data,
                    remark: res.data.remark || ''
                });
                this.newUserName = '';
                this.showToastMessage('用户添加成功', 'success');
            } catch (err) {
                console.error('添加用户失败:', err);
                this.showToastMessage('添加用户失败', 'error');
            }
        },
        // 切换用户启用/禁用状态
        async toggleUser(user) {
            try {
                const res = await this.$axios.put(`/api/users/${user.id}`, { enabled: !user.enabled });
                const index = this.users.findIndex(u => u.id === user.id);
                if (index !== -1) {
                    this.$set(this.users, index, {
                        ...res.data,
                        remark: res.data.remark || ''
                    });
                }
                this.showToastMessage(`${user.name} 已${res.data.enabled ? '启用' : '禁用'}`, 'success');
            } catch (err) {
                console.error('切换状态失败:', err);
                this.showToastMessage('操作失败', 'error');
            }
        },
        // 重置用户 token（通过确认弹窗）
        resetToken(user) {
            this.confirmDialog = {
                visible: true,
                title: '重置 Token',
                message: `确定重置 ${user.name} 的 Token？旧的订阅链接将失效！`,
                icon: 'key',
                iconColor: 'var(--color-primary)',
                confirmText: '确认重置',
                onConfirm: async () => {
                    this.confirmDialog.visible = false;
                    try {
                        const res = await this.$axios.post(`/api/users/${user.id}/reset-token`);
                        const index = this.users.findIndex(u => u.id === user.id);
                        if (index !== -1) {
                            this.$set(this.users, index, {
                                ...res.data,
                                remark: res.data.remark || ''
                            });
                        }
                        this.showToastMessage(`${user.name} 的 Token 已重置`, 'success');
                    } catch (err) {
                        console.error('重置 Token 失败:', err);
                        this.showToastMessage('重置失败', 'error');
                    }
                }
            };
        },
        openRemarkDialog(user) {
            this.remarkTargetUser = user;
            this.remarkDraft = user.remark || '';
            this.remarkDialogVisible = true;
        },
        closeRemarkDialog() {
            if (this.savingRemark) return;
            this.remarkDialogVisible = false;
            this.remarkTargetUser = null;
            this.remarkDraft = '';
        },
        async saveRemark() {
            if (!this.remarkTargetUser || this.savingRemark) return;

            const normalizedRemark = this.remarkDraft.trim();
            const currentRemark = this.remarkTargetUser.remark || '';
            if (normalizedRemark === currentRemark) {
                this.closeRemarkDialog();
                return;
            }

            this.savingRemark = true;
            try {
                const res = await this.$axios.put(`/api/users/${this.remarkTargetUser.id}`, { remark: normalizedRemark });
                const index = this.users.findIndex(u => u.id === this.remarkTargetUser.id);
                if (index !== -1) {
                    this.$set(this.users, index, {
                        ...res.data,
                        remark: res.data.remark || ''
                    });
                }
                this.savingRemark = false;
                this.showToastMessage(normalizedRemark ? `${this.remarkTargetUser.name} 的备注已保存` : `${this.remarkTargetUser.name} 的备注已清除`, 'success');
                this.closeRemarkDialog();
            } catch (err) {
                console.error('更新备注失败:', err);
                const message = err.response?.data?.error || '备注保存失败';
                this.showToastMessage(message, 'error');
            } finally {
                this.savingRemark = false;
            }
        },
        // 删除用户（通过确认弹窗）
        confirmDeleteUser(user) {
            this.confirmDialog = {
                visible: true,
                title: '删除用户',
                message: `确定删除 ${user.name}？此操作不可撤销！`,
                icon: 'trash-2',
                iconColor: 'var(--color-danger-light)',
                confirmText: '确认删除',
                onConfirm: async () => {
                    this.confirmDialog.visible = false;
                    try {
                        await this.$axios.delete(`/api/users/${user.id}`);
                        this.users = this.users.filter(u => u.id !== user.id);
                        this.showToastMessage(`${user.name} 已删除`, 'success');
                    } catch (err) {
                        console.error('删除用户失败:', err);
                        this.showToastMessage('删除失败', 'error');
                    }
                }
            };
        },
        getUserNumber(user) {
            const index = this.users.findIndex(u => u.id === user.id);
            if (index !== -1) return index + 1;

            const fromName = String(user.name || '').match(/(\d+)$/);
            if (fromName) return parseInt(fromName[1], 10);

            const fromId = String(user.id || '').match(/(\d+)$/);
            if (fromId) return parseInt(fromId[1], 10);

            return 0;
        },
        getClashProfileName(user) {
            return `Airport-${String(this.getUserNumber(user)).padStart(3, '0')}.yaml`;
        },
        // 生成订阅链接 URL（统一输出 Clash YAML，各客户端通用）
        getSubscribeUrl(user) {
            const baseUrl = window.location.origin;
            return `${baseUrl}/api/subscribe/${user.token}/${this.getClashProfileName(user)}?target=clash`;
        },
        // 复制订阅链接
        async copyLink(user) {
            const url = this.getSubscribeUrl(user);
            try {
                await navigator.clipboard.writeText(url);
                this.showToastMessage(`${user.name} 的订阅链接已复制！`, 'success');
            } catch (err) {
                console.error('复制失败:', err);
                this.showToastMessage('复制失败，请手动复制', 'error');
            }
        },
        showToastMessage(message, type = 'success') {
            this.toastMessage = message;
            this.toastType = type;
            this.showToast = true;
            setTimeout(() => { this.showToast = false; }, 2500);
        },
        // 展开/收起访问记录
        async toggleAccessLogs(user) {
            const newState = !user._showLogs;
            this.$set(user, '_showLogs', newState);
            // 展开时加载数据
            if (newState) {
                this.$set(user, '_loadingLogs', true);
                try {
                    const res = await this.$axios.get(`/api/users/${user.id}/access-logs?limit=20`);
                    this.$set(user, '_accessLogs', res.data);
                } catch (err) {
                    console.error('获取访问记录失败:', err);
                    this.$set(user, '_accessLogs', []);
                } finally {
                    this.$set(user, '_loadingLogs', false);
                }
            }
        },
        // 格式化时间显示
        formatTime(isoStr) {
            const d = new Date(isoStr);
            const pad = n => String(n).padStart(2, '0');
            return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
        async replaceSubscriptionContent(content, userIds) {
            if (!content || !content.trim() || this.updatingSubscription) return;
            if (!userIds || userIds.length === 0) {
                this.showToastMessage('请至少选择一个用户', 'error');
                return;
            }
            this.updatingSubscription = true;
            this.updateResult = null;
            try {
                const res = await this.$axios.post('/api/subscription/update', { content, userIds, mode: this.subscriptionMode });
                this.updateResult = {
                    success: true,
                    message: res.data.message
                };
                this.showToastMessage(res.data.message, 'success');
                // 替换成功后清空待提交内容与选中
                this.pendingYamlContent = '';
                this.selectedYamlFileName = '';
                this.selectedTargetUserIds = [];
            } catch (err) {
                const msg = err.response?.data?.error || '替换失败';
                this.updateResult = { success: false, message: msg };
                this.showToastMessage(msg, 'error');
            } finally {
                this.updatingSubscription = false;
            }
        },
        // 选择 YAML 文件后仅暂存内容，不立即提交
        async onYamlFileSelected(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;

            this.selectedYamlFileName = file.name;
            this.subscriptionMode = 'server';
            try {
                this.pendingYamlContent = new TextDecoder('utf-8', { ignoreBOM: true }).decode(await file.arrayBuffer());
                this.updateResult = null;
            } catch (err) {
                const msg = err.message || '读取 YAML 文件失败';
                this.updateResult = { success: false, message: msg };
                this.showToastMessage(msg, 'error');
                this.pendingYamlContent = '';
            } finally {
                event.target.value = '';
            }
        },
        isTargetUserSelected(userId) {
            return this.selectedTargetUserIds.includes(userId);
        },
        toggleTargetUser(userId) {
            const idx = this.selectedTargetUserIds.indexOf(userId);
            if (idx === -1) {
                this.selectedTargetUserIds = [...this.selectedTargetUserIds, userId];
            } else {
                this.selectedTargetUserIds = this.selectedTargetUserIds.filter(id => id !== userId);
            }
        },
        toggleAllTargetUsers() {
            if (this.allTargetSelected) {
                this.selectedTargetUserIds = [];
            } else {
                this.selectedTargetUserIds = this.enabledUsers.map(u => u.id);
            }
        }
    },
    mounted() {
        // Mixin 已处理 fetchBackground() 和 triggerLoadAnimation()
        this.checkAuth();
    }
}
</script>

<style scoped>
/* ===== 基础容器 ===== */
.page-container {
    position: relative;
    min-height: 100vh;
    width: 100vw;
    overflow-x: hidden;
    font-family: var(--font-body);
    color: var(--color-ink);
}

/* ===== 粒子动画 ===== */
.particles {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    overflow: hidden;
    z-index: 1;
    pointer-events: none;
}

.particle {
    position: absolute;
    bottom: -20px;
    background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
    border-radius: 50%;
    animation: float-up linear infinite;
}

@keyframes float-up {
    0% { transform: translateY(0); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(-100vh); opacity: 0; }
}

/* ===== 渐变遮罩 ===== */
.gradient-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg, rgba(26, 26, 46, 0.75) 0%, rgba(22, 33, 62, 0.65) 100%);
    z-index: 2;
    pointer-events: none;
}

/* ===== 内容包装器 ===== */
.content-wrapper {
    position: relative;
    z-index: 10;
    padding: 20px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* ===== 返回按钮 ===== */
.back-button {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 0.95rem;
    margin-bottom: 20px;
    transition: all 0.3s ease;
}

.back-button:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
    color: #fff;
}

.logout-button {
    align-self: flex-end;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 20px;
}

.logout-button:hover {
    background: rgba(244, 67, 54, 0.12);
    border-color: rgba(244, 67, 54, 0.3);
    color: #e57373;
}

/* ===== 登录区域 ===== */
.login-section {
    text-align: center;
}

.icon-wrapper.icon-lock {
    background: linear-gradient(135deg, #ffd166 0%, #ff9f43 100%);
    box-shadow: 0 4px 20px rgba(255, 209, 102, 0.4);
}

.login-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 360px;
    margin: 0 auto;
}

.login-btn {
    margin-top: 4px;
}

.login-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 14px;
    background: rgba(244, 67, 54, 0.12);
    border: 1px solid rgba(244, 67, 54, 0.3);
    border-radius: 10px;
    color: #e57373;
    font-size: 0.85rem;
}

/* ===== 玻璃态卡片 ===== */
.glass-card {
    width: 100%;
    max-width: 700px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: 24px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    padding: 40px 32px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    transform: translateY(30px) scale(0.95);
    opacity: 0;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card.show {
    transform: translateY(0) scale(1);
    opacity: 1;
}

/* ===== 页面头部 ===== */
.page-header {
    text-align: center;
    margin-bottom: 24px;
}

.icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #6ee7ff 0%, #3ab7ff 100%);
    border-radius: 20px;
    margin-bottom: 16px;
    box-shadow: 0 4px 20px rgba(110, 231, 255, 0.4);
}

.page-icon {
    font-size: 28px;
}

.title {
    font-size: 2rem;
    font-weight: 700;
    background: linear-gradient(90deg, #6ee7ff, #9ef3ff, #ffd166, #3ab7ff);
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradient-flow 4s ease infinite;
    margin-bottom: 8px;
}

@keyframes gradient-flow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
}

.subtitle {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.95rem;
}

/* ===== 分隔线 ===== */
.divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin: 24px 0;
}

.divider-line {
    height: 1px;
    width: 60px;
    background: linear-gradient(90deg, transparent, rgba(110, 231, 255, 0.5), transparent);
}

.divider-dot {
    width: 6px;
    height: 6px;
    background: linear-gradient(135deg, #6ee7ff, #ffd166);
    border-radius: 50%;
}

/* ===== 添加用户区域 ===== */
.add-user-section {
    margin-bottom: 16px;
}

/* ===== 一键提取节点 ===== */
.update-sub-section {
    margin-bottom: 24px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.section-label {
    font-size: 0.92rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 12px;
}

.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.section-head .section-label {
    margin-bottom: 0;
}

.rules-entry-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    background: rgba(110, 231, 255, 0.12);
    border: 1px solid rgba(110, 231, 255, 0.26);
    border-radius: var(--radius-pill);
    color: #6ee7ff;
    font-size: 0.78rem;
    font-weight: 600;
    text-decoration: none;
    transition: all var(--duration-normal) ease;
}

.rules-entry-link:hover {
    background: rgba(110, 231, 255, 0.22);
    box-shadow: 0 4px 16px rgba(110, 231, 255, 0.18);
}

.update-btn {
    background: linear-gradient(135deg, rgba(255, 193, 7, 0.25), rgba(255, 152, 0, 0.25)) !important;
    border-color: rgba(255, 193, 7, 0.4) !important;
}

.update-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(255, 193, 7, 0.4), rgba(255, 152, 0, 0.4)) !important;
    box-shadow: 0 4px 16px rgba(255, 193, 7, 0.25) !important;
}

.subscription-source-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.subscription-source-panel {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    background: rgba(10, 16, 28, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
}

.source-title {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.86rem;
    font-weight: 600;
}

.subscription-textarea {
    min-height: 112px;
    resize: vertical;
    line-height: 1.5;
    font-family: var(--font-mono);
    font-size: 0.82rem;
}

.yaml-file-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    min-height: 48px;
    background: rgba(110, 231, 255, 0.12);
    border: 1px solid rgba(110, 231, 255, 0.26);
    border-radius: 12px;
    color: #6ee7ff;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.yaml-file-button:hover:not(.disabled) {
    background: rgba(110, 231, 255, 0.22);
    box-shadow: 0 4px 16px rgba(110, 231, 255, 0.18);
}

.yaml-file-button input {
    display: none;
}

.yaml-file-button.disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.selected-file-name {
    padding: 8px 10px;
    background: rgba(0, 0, 0, 0.18);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.58);
    font-family: var(--font-mono);
    font-size: 0.76rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ===== 替换目标用户选择 ===== */
.target-users-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    background: rgba(0, 0, 0, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
}

.target-users-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.target-title {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 600;
}

.select-all-btn {
    background: rgba(110, 231, 255, 0.1);
    border: 1px solid rgba(110, 231, 255, 0.24);
    border-radius: 6px;
    color: #6ee7ff;
    font-size: 0.74rem;
    padding: 4px 10px;
    cursor: pointer;
    transition: all 0.25s ease;
}

.select-all-btn:hover:not(:disabled) {
    background: rgba(110, 231, 255, 0.2);
}

.select-all-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.target-users-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 6px;
}

.target-user-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}

.target-user-chip:hover {
    background: rgba(255, 255, 255, 0.07);
}

.target-user-chip.selected {
    background: rgba(255, 193, 7, 0.16);
    border-color: rgba(255, 193, 7, 0.4);
}

.target-user-chip input {
    margin: 0;
    accent-color: #ffc107;
}

.target-user-chip .chip-name {
    flex: 1;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.85);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.target-user-chip .chip-no {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.4);
}

.target-hint {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.4);
}

.replace-submit-btn {
    margin-top: 4px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 46px;
    font-size: 0.92rem;
    font-weight: 600;
    border-radius: 12px;
    border: 1px solid rgba(255, 193, 7, 0.4);
    cursor: pointer;
    transition: all 0.3s ease;
}

.replace-submit-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.update-result {
    margin-top: 12px;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 0.9rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.result-success {
    background: rgba(76, 175, 80, 0.12);
    border: 1px solid rgba(76, 175, 80, 0.3);
    color: #81c784;
}

.result-error {
    background: rgba(244, 67, 54, 0.12);
    border: 1px solid rgba(244, 67, 54, 0.3);
    color: #e57373;
}

.result-icon {
    font-size: 1rem;
}

.add-user-form {
    display: flex;
    gap: 12px;
}

.input-field {
    flex: 1;
    padding: 12px 18px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    color: #fff;
    font-size: 0.95rem;
    outline: none;
    transition: all 0.3s ease;
}

.input-field::placeholder {
    color: rgba(255, 255, 255, 0.4);
}

.input-field:focus {
    border-color: rgba(110, 231, 255, 0.6);
    box-shadow: 0 0 12px rgba(110, 231, 255, 0.15);
}

.add-btn {
    padding: 12px 24px;
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.3), rgba(58, 183, 255, 0.3));
    border: 1px solid rgba(110, 231, 255, 0.4);
    border-radius: 12px;
    color: #fff;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
}

.add-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.5), rgba(58, 183, 255, 0.5));
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(110, 231, 255, 0.3);
}

.add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

/* ===== 加载状态 ===== */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 40px 0;
    color: rgba(255, 255, 255, 0.6);
}

.spinner {
    width: 36px;
    height: 36px;
    border: 3px solid rgba(110, 231, 255, 0.2);
    border-top-color: #6ee7ff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* ===== 用户卡片列表 ===== */
.users-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.user-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 20px;
    opacity: 0;
    transform: translateY(15px);
    transition: all 0.4s ease;
    transition-delay: var(--delay);
}

.user-card.show {
    opacity: 1;
    transform: translateY(0);
}

.user-card:hover {
    border-color: rgba(110, 231, 255, 0.3);
    box-shadow: 0 4px 20px rgba(110, 231, 255, 0.1);
}

.user-card.disabled-card {
    opacity: 0.6;
}

.user-card.disabled-card.show {
    opacity: 0.6;
}

/* ===== 用户头部 ===== */
.user-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
}

.user-avatar {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: linear-gradient(135deg, #6ee7ff, #3ab7ff);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    font-weight: 700;
    color: #0a101c;
    flex-shrink: 0;
}

.user-avatar.avatar-disabled {
    background: linear-gradient(135deg, #666, #444);
}

.user-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.user-name {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
}

.user-meta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.user-number-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(110, 231, 255, 0.1);
    border: 1px solid rgba(110, 231, 255, 0.18);
    color: rgba(110, 231, 255, 0.82);
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.user-token {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.4);
    font-family: 'Courier New', monospace;
    overflow: hidden;
    text-overflow: ellipsis;
}

.remark-btn {
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.remark-btn:hover {
    background: rgba(110, 231, 255, 0.12);
    border-color: rgba(110, 231, 255, 0.22);
    color: #6ee7ff;
}

.user-remark {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 6px 10px;
    margin-top: 2px;
    border-radius: 10px;
    background: rgba(255, 193, 7, 0.08);
    border: 1px solid rgba(255, 193, 7, 0.16);
    color: rgba(255, 224, 130, 0.92);
    font-size: 0.78rem;
    line-height: 1.4;
    word-break: break-word;
}

.user-status {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
    flex-shrink: 0;
}

.status-active {
    background: rgba(76, 175, 80, 0.2);
    border: 1px solid rgba(76, 175, 80, 0.4);
    color: #81c784;
}

.status-inactive {
    background: rgba(244, 67, 54, 0.2);
    border: 1px solid rgba(244, 67, 54, 0.4);
    color: #e57373;
}

/* ===== 订阅链接区域 ===== */
.subscribe-links {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 14px;
}

.subscribe-links.disabled-notice {
    text-align: center;
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.88rem;
}

.link-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
}

.link-row + .link-row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    margin-top: 6px;
    padding-top: 10px;
}

.link-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
}

.link-url {
    padding: 8px 12px;
    margin: 6px 0;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 8px;
    font-size: 0.75rem;
    font-family: var(--font-mono);
    color: rgba(110, 231, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: all 0.3s ease;
}

.link-url:hover {
    background: rgba(110, 231, 255, 0.08);
    border-color: rgba(110, 231, 255, 0.2);
    color: #6ee7ff;
}

.copy-btn {
    padding: 6px 16px;
    background: rgba(110, 231, 255, 0.15);
    border: 1px solid rgba(110, 231, 255, 0.3);
    border-radius: 8px;
    color: #6ee7ff;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.copy-btn:hover {
    background: rgba(110, 231, 255, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 2px 10px rgba(110, 231, 255, 0.2);
}

/* ===== 操作按钮 ===== */
.user-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.action-btn {
    padding: 7px 14px;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid;
}

.toggle-btn.btn-warn {
    background: rgba(255, 152, 0, 0.12);
    border-color: rgba(255, 152, 0, 0.3);
    color: #ffb74d;
}

.toggle-btn.btn-warn:hover {
    background: rgba(255, 152, 0, 0.25);
}

.toggle-btn.btn-success {
    background: rgba(76, 175, 80, 0.12);
    border-color: rgba(76, 175, 80, 0.3);
    color: #81c784;
}

.toggle-btn.btn-success:hover {
    background: rgba(76, 175, 80, 0.25);
}

.btn-reset {
    background: rgba(110, 231, 255, 0.08);
    border-color: rgba(110, 231, 255, 0.2);
    color: rgba(110, 231, 255, 0.8);
}

.btn-reset:hover {
    background: rgba(110, 231, 255, 0.2);
}

.btn-danger {
    background: rgba(244, 67, 54, 0.08);
    border-color: rgba(244, 67, 54, 0.25);
    color: #e57373;
}

.btn-danger:hover {
    background: rgba(244, 67, 54, 0.2);
}

/* ===== 访问记录区域 ===== */
.access-logs-section {
    margin-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 10px;
}

.access-logs-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.88rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.access-logs-toggle:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
}

.toggle-arrow {
    font-size: 0.8rem;
    transition: transform 0.3s ease;
}

.toggle-arrow.arrow-open {
    transform: rotate(180deg);
}

.access-logs-content {
    margin-top: 10px;
    overflow: hidden;
}

.logs-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.85rem;
}

.spinner-sm {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(110, 231, 255, 0.2);
    border-top-color: #6ee7ff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.logs-empty {
    text-align: center;
    padding: 16px;
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.85rem;
}

.logs-table-wrapper {
    overflow-x: auto;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.logs-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
}

.logs-table thead {
    background: rgba(255, 255, 255, 0.06);
}

.logs-table th {
    padding: 8px 10px;
    text-align: left;
    color: rgba(255, 255, 255, 0.55);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
}

.logs-table td {
    padding: 8px 10px;
    color: rgba(255, 255, 255, 0.7);
    border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.logs-table tbody tr:hover {
    background: rgba(110, 231, 255, 0.04);
}

.log-ip {
    font-family: 'Courier New', monospace;
    color: rgba(110, 231, 255, 0.8);
    font-size: 0.78rem;
}

.log-location {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.target-tag {
    display: inline-block;
    padding: 2px 8px;
    background: rgba(255, 193, 7, 0.12);
    border: 1px solid rgba(255, 193, 7, 0.25);
    border-radius: 4px;
    font-size: 0.72rem;
    color: #ffd166;
    text-transform: capitalize;
}

.log-time {
    white-space: nowrap;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.45);
}

/* 访问记录展开/收起动画 */
.logs-slide-enter-active {
    transition: all 0.3s ease;
    max-height: 500px;
}

.logs-slide-leave-active {
    transition: all 0.3s ease;
    max-height: 0;
}

.logs-slide-enter, .logs-slide-leave-to {
    opacity: 0;
    max-height: 0;
}

/* ===== 空状态 ===== */
.empty-state {
    text-align: center;
    padding: 40px 0;
    color: rgba(255, 255, 255, 0.5);
}

.empty-icon {
    font-size: 3rem;
    display: block;
    margin-bottom: 12px;
}

/* ===== 底部提示 ===== */
.footer-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 28px;
    padding: 12px;
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.2);
    border-radius: 12px;
    color: rgba(255, 193, 7, 0.9);
    font-size: 0.88rem;
}

.tip-icon {
    font-size: 1rem;
}

/* ===== Toast 提示 ===== */
.toast {
    position: fixed;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 24px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(20px);
    border-radius: 14px;
    color: #fff;
    font-size: 1rem;
    z-index: 1000;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.toast.success {
    border: 1px solid rgba(76, 175, 80, 0.5);
}

.toast.error {
    border: 1px solid rgba(244, 67, 54, 0.5);
}

.toast-icon {
    font-size: 1.2rem;
}

.toast-enter-active, .toast-leave-active {
    transition: all 0.4s ease;
}

.toast-enter, .toast-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
}

.dialog-fade-enter-active, .dialog-fade-leave-active {
    transition: opacity 0.2s ease;
}

.dialog-fade-enter, .dialog-fade-leave-to {
    opacity: 0;
}

.remark-dialog-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(4, 10, 18, 0.7);
    backdrop-filter: blur(10px);
    z-index: 999;
}

.remark-dialog {
    width: min(100%, 460px);
    padding: 20px;
    border-radius: 18px;
    background: rgba(18, 28, 45, 0.96);
    border: 1px solid rgba(110, 231, 255, 0.16);
    box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
}

.remark-dialog-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.remark-dialog-title {
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
}

.remark-dialog-subtitle {
    margin-top: 4px;
    font-size: 0.84rem;
    color: rgba(255, 255, 255, 0.48);
}

.remark-dialog-close {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.66);
    font-size: 1.2rem;
    line-height: 1;
    cursor: pointer;
    transition: all 0.2s ease;
}

.remark-dialog-close:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
}

.remark-textarea {
    width: 100%;
    min-height: 120px;
    padding: 14px 16px;
    resize: vertical;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.92rem;
    line-height: 1.5;
    outline: none;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.remark-textarea:focus {
    border-color: rgba(110, 231, 255, 0.42);
    box-shadow: 0 0 0 4px rgba(110, 231, 255, 0.08);
}

.remark-textarea::placeholder {
    color: rgba(255, 255, 255, 0.32);
}

.remark-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 14px;
    flex-wrap: wrap;
}

.remark-counter {
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.42);
}

.remark-dialog-actions {
    display: flex;
    gap: 10px;
}

.remark-secondary-btn,
.remark-primary-btn {
    padding: 9px 16px;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.remark-secondary-btn {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.72);
}

.remark-secondary-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
}

.remark-primary-btn {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.3), rgba(58, 183, 255, 0.32));
    border: 1px solid rgba(110, 231, 255, 0.32);
    color: #fff;
}

.remark-primary-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(58, 183, 255, 0.18);
}

.remark-secondary-btn:disabled,
.remark-primary-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

/* ===== 响应式 ===== */
@media (max-width: 480px) {
    .glass-card {
        padding: 28px 16px;
        border-radius: 20px;
    }
    
    .title {
        font-size: 1.6rem;
    }
    
    .add-user-form {
        flex-direction: column;
    }

    .user-actions {
        justify-content: flex-start;
    }

    .action-btn {
        font-size: 0.78rem;
        padding: 6px 10px;
    }

    .user-header {
        align-items: flex-start;
    }

    .remark-dialog {
        padding: 18px 16px;
    }

    .remark-dialog-footer {
        align-items: stretch;
    }

    .remark-dialog-actions {
        width: 100%;
        justify-content: flex-end;
    }
}

@media (min-width: 481px) and (max-width: 768px) {
    .glass-card {
        max-width: 520px;
    }
}

@media (min-width: 1024px) {
    .glass-card {
        max-width: 720px;
        padding: 48px 40px;
    }
}

/* ===== 确认弹窗 ===== */
.modal-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1001;
}

.confirm-modal {
    width: 90%;
    max-width: 360px;
    background: linear-gradient(135deg, rgba(16, 24, 40, 0.92), rgba(20, 30, 52, 0.88));
    border: 1px solid rgba(110, 231, 255, 0.25);
    border-radius: var(--radius-xl);
    padding: 32px var(--space-2xl);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    text-align: center;
}

.confirm-header {
    margin-bottom: var(--space-2xl);
}

.confirm-header .lucide-icon {
    display: block;
    margin: 0 auto 12px;
}

.confirm-header h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: #fff;
    margin: 0 0 8px;
}

.confirm-header p {
    font-size: 0.92rem;
    color: var(--color-subtle);
    margin: 0;
    line-height: 1.5;
}

.confirm-footer {
    display: flex;
    gap: var(--space-md);
}

.modal-cancel, .modal-confirm {
    flex: 1;
    padding: var(--space-md);
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--duration-normal) ease;
    border: 1px solid;
}

.modal-cancel {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
}

.modal-cancel:hover {
    background: rgba(255, 255, 255, 0.12);
}

.confirm-danger {
    background: linear-gradient(135deg, rgba(244, 67, 54, 0.3), rgba(229, 115, 115, 0.3));
    border-color: rgba(244, 67, 54, 0.4);
    color: #fff;
}

.confirm-danger:hover {
    background: linear-gradient(135deg, rgba(244, 67, 54, 0.5), rgba(229, 115, 115, 0.5));
    box-shadow: 0 4px 16px rgba(244, 67, 54, 0.25);
}

.modal-enter-active, .modal-leave-active {
    transition: opacity var(--duration-normal) ease;
}
.modal-enter-active .confirm-modal, .modal-leave-active .confirm-modal {
    transition: transform var(--duration-normal) ease;
}
.modal-enter, .modal-leave-to {
    opacity: 0;
}
.modal-enter .confirm-modal {
    transform: scale(0.9) translateY(20px);
}
.modal-leave-to .confirm-modal {
    transform: scale(0.95) translateY(10px);
}

.logs-toggle-label {
    display: flex;
    align-items: center;
    gap: 6px;
}

.toggle-arrow {
    transition: transform var(--duration-normal) ease;
}

.toggle-arrow.arrow-open {
    transform: rotate(180deg);
}
</style>
